import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Download,
  Eye,
  Mail,
  AlertCircle,
  Loader2,
  Paperclip,
  Users,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  CalendarRange,
  List // Added icon for page size
} from 'lucide-react';

import { apiFetch } from '../api';
import AnimatedBackground from '../components/Background';
import EmailDetails from './EmailDetails';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';
import ExportBlockedModal from '../components/Exportblockedmodal';

export default function EmailHistory({ onBack, setError, loginId, deliveryMode }) {

  const { client, connected } = useWebSocket();

  const [emailData, setEmailData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Export-blocked modal
  const [showExportBlocked, setShowExportBlocked] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appNames, setAppNames] = useState([]);
  const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Now mutable
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [expandedEmails, setExpandedEmails] = useState(new Set());

  // Removed totalCount state usage for UI logic, 
  // though we can still store it if API sends it, we won't use it for validation.
  const [totalCount, setTotalCount] = useState(0);

  const fetchAppNames = async () => {
    setIsAppNamesLoading(true);
    setError('');

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          // This will try to connect and WAIT until it's ready
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setError('Socket connection failed. Please check your network.');
          setIsAppNamesLoading(false);
          return;
        }

        // 2. Define the response topic (Must match your Backend Controller)
        // Assuming loginId is available in your component scope
        const topic = `/topic/getEmailAppName/${loginId}`;

        // 3. Subscribe to listen for the response
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);

            // Handle stringified payload edge case
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            // 4. Map the data exactly like the API 'else' block does
            if (data && data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
              const names = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
              setAppNames(names);
              // Set default selection if not already set
              setSelectedApp(prev => prev || names[0]);
            } else {
              setError('Failed to load app names via socket. Invalid format.');
            }
          } catch (e) {
            console.error('Failed to parse socket app name response', e);
            setError('Failed to parse app name data.');
          } finally {
            // 5. Cleanup: Unsubscribe immediately since we only need this list once
            subscription.unsubscribe();
            setIsAppNamesLoading(false);
          }
        });

        // 6. Publish the request to the server
        // Destination must match your Spring Boot @MessageMapping
        try {
          client.publish({
            destination: `/app/getEmailAppName`,
            body: JSON.stringify({}) // Sending empty body or specific filter if backend requires it
          });
        } catch (e) {
          console.error('Failed to publish app name request', e);
          subscription.unsubscribe();
          setIsAppNamesLoading(false);
        }

      } else {
        // --- API LOGIC (Existing) ---
        const response = await apiFetch(`/getEmailAppName`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
          const appNames = ['ALL', ...data.Response.map(item => item.appName).filter(Boolean)];
          setAppNames(appNames);
          setSelectedApp(prev => prev || appNames[0]);
        } else {
          setError('Failed to load app names. Invalid response format.');
        }
      }
    } catch (err) {
      console.error('Error fetching app names:', err);
      setError('Failed to load app names. Network error or API issue.');
    } finally {
      // Only turn off loading here for API mode. 
      // For Socket mode, we turn it off inside the subscription callback.
      if (deliveryMode !== 'socket') {
        setIsAppNamesLoading(false);
      }
    }
  };

  const dateToMs = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').getTime().toString();
  };

  const endDateToMs = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T23:59:59').getTime().toString();
  };

  const fetchEmailHistory = async (page = currentPage, size = pageSize) => {
    setIsLoading(true);
    try {
      const filterPayload = {
        pageNo: page,
        pageSize: size
      };

      if (searchTerm.trim()) {
        filterPayload.searchText = searchTerm.trim();
      }

      if (referenceSearch.trim()) {
        filterPayload.referenceIdOrName = referenceSearch.trim();
      }

      if (selectedApp && selectedApp !== 'ALL') {
        filterPayload.appName = selectedApp;
      }

      if (statusFilter) {
        filterPayload.status = statusFilter;
      }

      if (startDate) {
        filterPayload.startTimeMs = dateToMs(startDate);
      }

      if (endDate) {
        filterPayload.endTimeMs = endDateToMs(endDate);
      }

      const url = `/email/filter`;
      if (deliveryMode == 'socket') {

        try {
          // This will try to connect and WAIT until it's ready
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setError('Socket connection failed. Please check your network.');
          setIsAppNamesLoading(false);
          return;
        }

        const topic = `/topic${url}/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data =
              payload && typeof payload === 'object'
                ? payload
                : typeof payload === 'string'
                  ? JSON.parse(payload)
                  : null;

            if (data && typeof data === 'object' && Array.isArray(data.Response)) {
              setEmailData(data.Response);
              setTotalCount(Number(data.TOTAL_COUNT) || 0);
            } else {
              setEmailData([]);
              setTotalCount(0);
            }
          } catch (e) {
            console.error('Failed to parse history batch', e);
            setError('Failed to parse history data from server.');
            setEmailData([]);
            setTotalCount(0);
          }
        });

        try {
          client.publish({
            destination: `/app${url}`,
            body: JSON.stringify(filterPayload)
          });
        } catch (e) {
          console.error('Failed to send request', e);
          setError('Failed to request history data via socket.');
          setIsLoading(false);
          setIsStreaming(false);
          setEmailData([]);
          setTotalCount(0);
        }

        return () => {
          subscription?.unsubscribe();
          setIsStreaming(false);
        };

      } else {
        const response = await apiFetch(url, {
          method: 'POST',
          body: JSON.stringify(filterPayload)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data && typeof data === 'object') {
          if (data.Response && Array.isArray(data.Response)) {
            setEmailData(data.Response);
            // We keep this just in case, but won't rely on it for pagination buttons
            setTotalCount(data.TOTAL_COUNT || 0);
          } else {
            setEmailData([]);
            setTotalCount(0);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
      setError('Failed to fetch email history. Please try again.');
      setEmailData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // REPLACE ALL THREE useEffects WITH THIS ONE:
  useEffect(() => {
    // Don't fetch until selectedApp is initialized
    if (!selectedApp) {
      return;
    }

    // Debounce for text searches (500ms), immediate for dropdowns/dates (0ms)
    const delay = searchTerm || referenceSearch ? 500 : 0;

    const debounceTimer = setTimeout(() => {
      fetchEmailHistory(currentPage, pageSize);
    }, delay);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, referenceSearch, startDate, endDate, statusFilter, selectedApp, currentPage, pageSize]);

  // Initial load - fetch app names only
  useEffect(() => {
    fetchAppNames();
  }, []);

  const handleRefresh = () => {
    fetchEmailHistory(currentPage, pageSize);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setReferenceSearch('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setSelectedApp('ALL');
    setCurrentPage(0);
    setPageSize(10);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setIsLoading(true); // Lock UI immediately
    setPageSize(newSize);
    setCurrentPage(0); // Reset to 0 (first page)
  };

  const handlePreviousPage = () => {
    // Check > 0 because this specific component uses 0-based indexing
    if (currentPage > 0 && !isLoading) {
      setIsLoading(true); // Lock UI immediately
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    // Only proceed if full page of data received AND not loading
    if (emailData.length >= pageSize && !isLoading) {
      setIsLoading(true); // Lock UI immediately
      setCurrentPage(currentPage + 1);
    }
  };

  const handleExport = async () => {

    // Block export in socket/stream mode — show modal instead
    if (deliveryMode === 'socket') {
      setShowExportBlocked(true);
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates for export');
      return;
    }

    setIsExporting(true);
    try {
      const filterPayload = {
        pageNo: 0,
        pageSize: 1000 // Default high limit for export
      };

      if (searchTerm.trim()) {
        filterPayload.searchText = searchTerm.trim();
      }

      if (referenceSearch.trim()) {
        filterPayload.referenceIdOrName = referenceSearch.trim();
      }

      if (selectedApp && selectedApp !== 'ALL') {
        filterPayload.appName = selectedApp;
      }

      if (statusFilter) {
        filterPayload.status = statusFilter;
      }

      filterPayload.startTimeMs = dateToMs(startDate);
      filterPayload.endTimeMs = endDateToMs(endDate);

      const url = `/email/filter?export=true`;

      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(filterPayload)
      });

      if (!res.ok) {
        throw new Error(`Export failed: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();

      if (data.STATUS === 'SUCCESS' && data.Response) {
        const byteCharacters = atob(data.Response);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `email-history-${startDate}-to-${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Export failed: Invalid response format');
      }

    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'text-green-400';
      case 'FAILED': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS': return CheckCircle;
      case 'FAILED': return XCircle;
      default: return Clock;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'SUCCESS': return 'bg-green-500/20 border-green-500/30';
      case 'FAILED': return 'bg-red-500/20 border-red-500/30';
      default: return 'bg-yellow-500/20 border-yellow-500/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateStr;
    }
  };

  const truncateText = (text, length = 50) => {
    if (!text) return '';
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const parseEmailList = (emailString) => {
    if (!emailString) return [];
    return emailString.split(',').map(email => email.trim()).filter(Boolean);
  };

  const toggleEmailExpansion = (emailId) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const isExportEnabled = startDate && endDate && !isExporting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8]">
      <AnimatedBackground />

      {/* Export-blocked modal — shown when in socket mode */}
      <ExportBlockedModal
        isOpen={showExportBlocked}
        onClose={() => setShowExportBlocked(false)}
        feature="Email Export"
      />

      {/* Header */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200 text-sm sm:text-base"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">Email History</h1>
                  <p className="text-xs sm:text-sm text-white/60 hidden sm:block">Email Communication History</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={handleResetFilters}
                className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-orange-500/20 text-orange-200 rounded-lg hover:bg-orange-500/30 transition-all duration-200 text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline">Reset</span>
              </button>
              <button
                onClick={handleRefresh}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 transition-all duration-200 text-sm"
              >
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={handleExport}
                disabled={!isExportEnabled && deliveryMode !== 'socket'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${deliveryMode === 'socket'
                  ? 'bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 cursor-pointer'
                  : isExportEnabled
                    ? 'bg-green-500/20 text-green-200 hover:bg-green-500/30'
                    : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export'}</span>
                {deliveryMode === 'socket' && (
                  <span className="ml-1 text-[10px] font-bold bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full border border-orange-500/30">
                    API only
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 relative z-10">
        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Search by Email/Subject */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Search Email/Subject</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search emails..."
                  className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
            </div>

            {/* Search by Reference ID or Name */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Reference ID or Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={referenceSearch}
                  onChange={(e) => setReferenceSearch(e.target.value)}
                  placeholder="Search by reference..."
                  className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            {/* App Name Filter */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">App Name</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <select
                  value={selectedApp}
                  onChange={(e) => setSelectedApp(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  {appNames.length === 0 ? (
                    <option className="bg-slate-800" disabled>Loading...</option>
                  ) : (
                    appNames.map((app, index) => (
                      <option key={index} value={app} className="bg-slate-800">
                        {app === 'ALL' ? 'All Apps' : app}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">End Date</label>
              <div className="relative">
                <CalendarRange className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  <option value="" className="bg-slate-800">All Status</option>
                  <option value="SUCCESS" className="bg-slate-800">Success</option>
                  <option value="FAILED" className="bg-slate-800">Failed</option>
                  <option value="PENDING" className="bg-slate-800">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Export Info */}
          {!isExportEnabled && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-200 text-sm">
                  Please select both start and end dates to enable export functionality
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10 bg-white/5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <h2 className="text-xl font-bold text-white">
                Email History
                <span className="ml-3 text-sm text-white/60">
                  Showing {emailData.length} record{emailData.length !== 1 ? 's' : ''}
                </span>
              </h2>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16 sm:py-20">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="w-8 h-8 animate-spin text-green-400" />
                <span className="text-white/80 text-sm sm:text-base">Loading email history...</span>
              </div>
            </div>
          ) : emailData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-20">
              <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-white/40 mb-4" />
              <p className="text-white/60 text-lg sm:text-xl mb-2">No emails found</p>
              <p className="text-white/40 text-sm">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {emailData.map((email) => {
                const StatusIcon = getStatusIcon(email.STATUS);
                const isExpanded = expandedEmails.has(email._id);
                const recipients = parseEmailList(email.to);
                const ccRecipients = parseEmailList(email.cc);
                const hasAttachments = email.email_attachment || email.attachment_data?.length > 0;

                return (
                  <div
                    key={email._id}
                    className="px-4 sm:px-6 py-4 sm:py-6 hover:bg-white/5 transition-all duration-200"
                  >
                    <div className="space-y-4">
                      {/* Main Email Info */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start space-x-3 sm:space-x-4">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-white font-semibold text-sm sm:text-base line-clamp-2">
                                    {email.subject || 'No Subject'}
                                  </h3>
                                  <p className="text-white/60 text-xs sm:text-sm mt-1">
                                    AppName: <span className="text-green-400 font-medium">{email.appName || 'N/A'}</span>
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2 flex-shrink-0">
                                  <div className={`px-2 sm:px-3 py-1 rounded-full border ${getStatusBg(email.STATUS)} flex items-center space-x-1`}>
                                    <StatusIcon className={`w-3 h-3 sm:w-4 sm:h-4 ${getStatusColor(email.STATUS)}`} />
                                    <span className={`text-xs font-medium ${getStatusColor(email.STATUS)}`}>
                                      {email.STATUS}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-white/60">
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span>{formatDate(email.creation_time_str)}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span>{recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</span>
                                </div>
                                {hasAttachments && (
                                  <div className="flex items-center space-x-1 text-blue-400">
                                    <Paperclip className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span>Attachments</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Preview */}
                      <div className="pl-10 sm:pl-16 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs sm:text-sm">
                          <div>
                            <p className="text-white/40 uppercase tracking-wide">To</p>
                            <p className="text-white/80 truncate" title={recipients.join(', ')}>
                              {truncateText(recipients.join(', '), 30)}
                            </p>
                          </div>
                          {ccRecipients.length > 0 && (
                            <div>
                              <p className="text-white/40 uppercase tracking-wide">CC</p>
                              <p className="text-white/80 truncate" title={ccRecipients.join(', ')}>
                                {truncateText(ccRecipients.join(', '), 30)}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-white/40 uppercase tracking-wide">Date</p>
                            <p className="text-white/80">{email.date}</p>
                          </div>
                        </div>

                        {/* Email Body Preview */}
                        <div>
                          <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message Preview</p>
                          <p className="text-white/80 text-sm line-clamp-2">
                            {email.body_type ? 'HTML Email Content' : ('TEXT message body')}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                          <button
                            onClick={() => setSelectedEmail(email)}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 transition-all duration-200 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View Details</span>
                          </button>
                          <button
                            onClick={() => toggleEmailExpansion(email._id)}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-white/10 text-white/80 rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            <span>{isExpanded ? 'Show Less' : 'Show More'}</span>
                          </button>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="space-y-4 p-4 bg-white/5 rounded-lg border border-white/10">
                            {/* Full Recipients */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-white/40 uppercase tracking-wide text-xs mb-1">All Recipients</p>
                                <div className="space-y-1">
                                  {recipients.map((recipient, index) => (
                                    <p key={index} className="text-white/80 font-mono text-xs">{recipient}</p>
                                  ))}
                                </div>
                              </div>
                              {ccRecipients.length > 0 && (
                                <div>
                                  <p className="text-white/40 uppercase tracking-wide text-xs mb-1">CC Recipients</p>
                                  <div className="space-y-1">
                                    {ccRecipients.map((recipient, index) => (
                                      <p key={index} className="text-white/80 font-mono text-xs">{recipient}</p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Attachments */}
                            {hasAttachments && (
                              <div>
                                <p className="text-white/40 uppercase tracking-wide text-xs mb-2">Attachments</p>
                                <div className="space-y-2">
                                  {email.attachment_format && (
                                    <div className="flex items-center space-x-2 p-2 bg-blue-500/20 rounded-lg">
                                      <Paperclip className="w-4 h-4 text-blue-400" />
                                      <span className="text-blue-200 text-sm">Format: {email.attachment_format}</span>
                                      {email.email_attachment_compress && (
                                        <span className="text-xs bg-blue-500/30 px-2 py-1 rounded text-blue-200">Compressed</span>
                                      )}
                                    </div>
                                  )}
                                  {email.attachment_data?.length > 0 && (
                                    <div className="text-white/60 text-sm">
                                      Attachment data: {email.attachment_data.length} record{email.attachment_data.length !== 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Full Message Body */}
                            <div>
                              <p className="text-white/40 uppercase tracking-wide text-xs mb-2">Full Message</p>
                              <div className="p-3 bg-white/10 rounded-lg border border-white/20">
                                <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">
                                  {email.body_type ? 'HTML Email Content (Click "View Details" to preview)' : (email.body || 'No message body')}
                                </p>
                              </div>
                            </div>

                            {/* Response */}
                            {email.email_response && (
                              <div>
                                <p className="text-white/40 uppercase tracking-wide text-xs mb-2">Server Response</p>
                                <div className={`p-3 rounded-lg border ${email.STATUS === 'FAILED' ? 'bg-red-500/20 border-red-500/30' : 'bg-green-500/20 border-green-500/30'}`}>
                                  <p className={`text-sm ${email.STATUS === 'FAILED' ? 'text-red-200' : 'text-green-200'}`}>{email.email_response}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && emailData.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-white/10 bg-white/5">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">

                {/* Page Size Selector */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 text-white/60 text-sm">
                    <List className="w-4 h-4" />
                    <span>Rows per page:</span>
                  </div>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="bg-white/10 border border-white/20 text-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value={10} className="bg-slate-800">10</option>
                    <option value={25} className="bg-slate-800">25</option>
                    <option value={50} className="bg-slate-800">50</option>
                    <option value={100} className="bg-slate-800">100</option>
                  </select>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 0 || isLoading}
                    className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                  >
                    Previous
                  </button>

                  <span className="text-white/80 px-3 py-2 text-sm font-medium">
                    Page {currentPage + 1}
                  </span>

                  <button
                    onClick={handleNextPage}
                    /* Updated Logic: 
                      1. emailData.length < pageSize: End of list
                      2. isLoading: Prevent double clicks or clicking while fetching
                    */
                    disabled={emailData.length < pageSize || isLoading}
                    className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedEmail && (
        <EmailDetails
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          setError={setError}
          loginId={loginId}
          deliveryMode={deliveryMode}
        />
      )}
    </div>
  );
}